<script setup lang="ts">
import { ref, computed } from 'vue'
import { Search, LayoutGrid, ArrowRight } from 'lucide-vue-next'
import { DialogHeader, DialogTitle } from '@/shared/components/ui/dialog'
import AppDialog from './AppDialog.vue'
import AppInput from '@/shared/components/common/AppInput.vue'
import { PRIMARY_NAV_ITEMS, SECONDARY_MENU_DATA } from '@/shared/constants/navigation'
import { usePermissions, buildRouteWithListScopeFromRoute, AppLoader } from '@/shared'
import { useRouter, useRoute } from 'vue-router'
import type { Component } from 'vue'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'update:open', value: boolean): void }>()

const openModel = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value)
})

interface NavSearchEntry {
  moduleId: string
  moduleLabel: string
  moduleIcon: Component
  groupLabel?: string
  label: string
  href: string
  disabled?: boolean
}

const query = ref('')
const { isVisible } = usePermissions()
const router = useRouter()
const route = useRoute()
const isNavigating = ref(false)

// ─── Flat navigation list built from constants ───────────────────────────────
const allEntries = computed<NavSearchEntry[]>(() => {
  const entries: NavSearchEntry[] = []

  for (const navItem of PRIMARY_NAV_ITEMS) {
    if (!isVisible(navItem)) continue

    const moduleData = SECONDARY_MENU_DATA[navItem.id]
    if (!moduleData) continue
    if (!isVisible(moduleData)) continue

    for (const item of moduleData.items) {
      if (!isVisible(item)) continue

      // Top-level item (no children) → add directly
      if (!item.items || item.items.length === 0) {
        entries.push({
          moduleId: navItem.id,
          moduleLabel: navItem.label,
          moduleIcon: navItem.icon as Component,
          label: item.label,
          href: item.href,
          disabled: item.disabled
        })
      } else {
        // Parent with children → add the children, labelling group after parent
        for (const child of item.items) {
          if (!isVisible(child)) continue

          entries.push({
            moduleId: navItem.id,
            moduleLabel: navItem.label,
            moduleIcon: navItem.icon as Component,
            groupLabel: item.label,
            label: child.label,
            href: child.href,
            disabled: child.disabled
          })
        }
      }
    }
  }

  return entries
})

// ─── Filtered results ─────────────────────────────────────────────────────────
const filteredEntries = computed<NavSearchEntry[]>(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return allEntries.value
  return allEntries.value.filter(
    (e) =>
      e.label.toLowerCase().includes(q) ||
      e.moduleLabel.toLowerCase().includes(q) ||
      (e.groupLabel?.toLowerCase().includes(q) ?? false)
  )
})

// ─── Grouped results for display (grouped by moduleLabel) ────────────────────
interface Group {
  moduleId: string
  moduleLabel: string
  moduleIcon: Component
  items: NavSearchEntry[]
}

const groupedResults = computed<Group[]>(() => {
  const map = new Map<string, Group>()
  for (const entry of filteredEntries.value) {
    if (!map.has(entry.moduleId)) {
      map.set(entry.moduleId, {
        moduleId: entry.moduleId,
        moduleLabel: entry.moduleLabel,
        moduleIcon: entry.moduleIcon,
        items: []
      })
    }
    map.get(entry.moduleId)!.items.push(entry)
  }
  return [...map.values()]
})

// ─── Handle entry click ───────────────────────────────────────────────────────
const handleSelect = async (entry: NavSearchEntry) => {
  if (entry.disabled) return
  isNavigating.value = true
  try {
    await router.push(buildRouteWithListScopeFromRoute(entry.href, route))
  } catch (error) {
    console.error('Navigation failed:', error)
  } finally {
    isNavigating.value = false
    emit('update:open', false)
  }
}
</script>

<template>
  <AppDialog v-model:is-open="openModel" size="md">
    <template #header>
      <DialogHeader
        class="px-4 py-4 border-b border-border flex flex-row items-center justify-between bg-card shrink-0"
      >
        <DialogTitle class="text-base font-semibold leading-none">Search Navigation</DialogTitle>
      </DialogHeader>
    </template>

    <div class="-mx-6 -mt-6 -mb-8 flex flex-col overflow-hidden">
      <!-- Search Input Row -->
      <div class="px-4 py-3 border-b border-border">
        <AppInput
          v-model="query"
          placeholder="Search navigation..."
          :left-icon="Search"
          class="h-10 border-0 bg-transparent shadow-none focus-visible:ring-0 text-base placeholder:text-muted-foreground"
        />
      </div>

      <!-- Results List -->
      <div class="app-scrollbar max-h-[60vh] overflow-y-auto">
        <!-- Empty state -->
        <div
          v-if="groupedResults.length === 0"
          class="flex flex-col items-center justify-center py-12 text-muted-foreground"
        >
          <Search class="h-10 w-10 mb-3 opacity-30" />
          <p class="text-sm font-medium">No results for "{{ query }}"</p>
          <p class="text-xs mt-1 opacity-70">Try a different search term.</p>
        </div>

        <!-- Grouped results -->
        <div v-for="group in groupedResults" :key="group.moduleId">
          <!-- Module group header -->
          <div class="flex items-center gap-2 px-4 pt-3 pb-1 sticky top-0 bg-popover z-10">
            <component :is="group.moduleIcon" class="h-3.5 w-3.5 text-primary shrink-0" />
            <span class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {{ group.moduleLabel }}
            </span>
          </div>

          <!-- Items inside the group -->
          <button
            v-for="entry in group.items"
            :key="`${group.moduleId}-${entry.label}`"
            type="button"
            :disabled="entry.disabled"
            :class="[
              'group w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors focus-visible:outline-none',
              entry.disabled
                ? 'cursor-not-allowed opacity-50 pointer-events-none'
                : 'cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground'
            ]"
            @click="handleSelect(entry)"
          >
            <div class="flex flex-col min-w-0">
              <span class="font-medium truncate">{{ entry.label }}</span>
              <span v-if="entry.groupLabel" class="text-xs text-muted-foreground truncate">
                {{ entry.groupLabel }}
              </span>
            </div>
            <ArrowRight
              v-if="!entry.disabled"
              class="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </button>

          <!-- Divider between groups -->
          <div class="mx-4 my-1 border-t border-border/50" />
        </div>
      </div>

      <div
        class="flex items-center border-t border-border bg-muted/40 px-4 py-2 text-[11px] text-muted-foreground"
      >
        <span class="flex items-center gap-1">
          <LayoutGrid class="h-3 w-3" />
          {{ allEntries.length }} nav items
        </span>
      </div>

      <!-- Loading overlay during redirection -->
      <AppLoader :loading="isNavigating" overlay text="Redirecting..." />
    </div>
  </AppDialog>
</template>
